/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `dive_settings` message.
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class DiveSettingsMesg : Mesg {
    public constructor() : super("DiveSettings", Profile.MesgNum.DIVE_SETTINGS)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** message_index. */
    public var messageIndex: UShort?
        get() = getFieldValue(MESSAGE_INDEX_FIELD_NUM) as? UShort
        set(value) = setFieldValue(MESSAGE_INDEX_FIELD_NUM, value)

    /** name. */
    public var name: String?
        get() = getFieldValue(NAME_FIELD_NUM) as? String
        set(value) = setFieldValue(NAME_FIELD_NUM, value)

    /** model. */
    public var model: TissueModelType?
        get() = (getFieldValue(MODEL_FIELD_NUM) as? UByte)?.let { TissueModelType.fromValue(it) }
        set(value) = setFieldValue(MODEL_FIELD_NUM, value?.value)

    /** gf_low, in percent. */
    public var gfLow: UByte?
        get() = getFieldValue(GF_LOW_FIELD_NUM) as? UByte
        set(value) = setFieldValue(GF_LOW_FIELD_NUM, value)

    /** gf_high, in percent. */
    public var gfHigh: UByte?
        get() = getFieldValue(GF_HIGH_FIELD_NUM) as? UByte
        set(value) = setFieldValue(GF_HIGH_FIELD_NUM, value)

    /** water_type. */
    public var waterType: WaterType?
        get() = (getFieldValue(WATER_TYPE_FIELD_NUM) as? UByte)?.let { WaterType.fromValue(it) }
        set(value) = setFieldValue(WATER_TYPE_FIELD_NUM, value?.value)

    /** water_density, in kg/m^3. Fresh water is usually 1000; salt water is usually 1025 */
    public var waterDensity: Float?
        get() = getFieldValue(WATER_DENSITY_FIELD_NUM) as? Float
        set(value) = setFieldValue(WATER_DENSITY_FIELD_NUM, value)

    /** po2_warn, in percent. Typically 1.40 */
    public var po2Warn: Double?
        get() = getFieldValue(PO2_WARN_FIELD_NUM) as? Double
        set(value) = setFieldValue(PO2_WARN_FIELD_NUM, value)

    /** po2_critical, in percent. Typically 1.60 */
    public var po2Critical: Double?
        get() = getFieldValue(PO2_CRITICAL_FIELD_NUM) as? Double
        set(value) = setFieldValue(PO2_CRITICAL_FIELD_NUM, value)

    /** po2_deco, in percent. */
    public var po2Deco: Double?
        get() = getFieldValue(PO2_DECO_FIELD_NUM) as? Double
        set(value) = setFieldValue(PO2_DECO_FIELD_NUM, value)

    /** safety_stop_enabled. */
    public var safetyStopEnabled: Boolean?
        get() = (getFieldValue(SAFETY_STOP_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(SAFETY_STOP_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** bottom_depth. */
    public var bottomDepth: Float?
        get() = getFieldValue(BOTTOM_DEPTH_FIELD_NUM) as? Float
        set(value) = setFieldValue(BOTTOM_DEPTH_FIELD_NUM, value)

    /** bottom_time. */
    public var bottomTime: UInt?
        get() = getFieldValue(BOTTOM_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(BOTTOM_TIME_FIELD_NUM, value)

    /** apnea_countdown_enabled. */
    public var apneaCountdownEnabled: Boolean?
        get() = (getFieldValue(APNEA_COUNTDOWN_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(APNEA_COUNTDOWN_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** apnea_countdown_time. */
    public var apneaCountdownTime: UInt?
        get() = getFieldValue(APNEA_COUNTDOWN_TIME_FIELD_NUM) as? UInt
        set(value) = setFieldValue(APNEA_COUNTDOWN_TIME_FIELD_NUM, value)

    /** backlight_mode. */
    public var backlightMode: DiveBacklightMode?
        get() = (getFieldValue(BACKLIGHT_MODE_FIELD_NUM) as? UByte)?.let { DiveBacklightMode.fromValue(it) }
        set(value) = setFieldValue(BACKLIGHT_MODE_FIELD_NUM, value?.value)

    /** backlight_brightness. */
    public var backlightBrightness: UByte?
        get() = getFieldValue(BACKLIGHT_BRIGHTNESS_FIELD_NUM) as? UByte
        set(value) = setFieldValue(BACKLIGHT_BRIGHTNESS_FIELD_NUM, value)

    /** backlight_timeout. */
    public var backlightTimeout: UByte?
        get() = getFieldValue(BACKLIGHT_TIMEOUT_FIELD_NUM) as? UByte
        set(value) = setFieldValue(BACKLIGHT_TIMEOUT_FIELD_NUM, value)

    /** repeat_dive_interval, in s. Time between surfacing and ending the activity */
    public var repeatDiveInterval: UShort?
        get() = getFieldValue(REPEAT_DIVE_INTERVAL_FIELD_NUM) as? UShort
        set(value) = setFieldValue(REPEAT_DIVE_INTERVAL_FIELD_NUM, value)

    /** safety_stop_time, in s. Time at safety stop (if enabled) */
    public var safetyStopTime: UShort?
        get() = getFieldValue(SAFETY_STOP_TIME_FIELD_NUM) as? UShort
        set(value) = setFieldValue(SAFETY_STOP_TIME_FIELD_NUM, value)

    /** heart_rate_source_type. */
    public var heartRateSourceType: SourceType?
        get() = (getFieldValue(HEART_RATE_SOURCE_TYPE_FIELD_NUM) as? UByte)?.let { SourceType.fromValue(it) }
        set(value) = setFieldValue(HEART_RATE_SOURCE_TYPE_FIELD_NUM, value?.value)

    /** heart_rate_source. */
    public var heartRateSource: UByte?
        get() = getFieldValue(HEART_RATE_SOURCE_FIELD_NUM) as? UByte
        set(value) = setFieldValue(HEART_RATE_SOURCE_FIELD_NUM, value)

    /**
     * heart_rate_antplus_device_type: what heart_rate_source means when this message selects it.
     */
    public val heartRateAntplusDeviceType: AntplusDeviceType?
        get() = (getFieldValue(HEART_RATE_SOURCE_FIELD_NUM, 0, SubFieldSelector.Named("HeartRateAntplusDeviceType")) as? UByte)?.let { AntplusDeviceType.fromValue(it) }

    public fun setHeartRateAntplusDeviceType(value: AntplusDeviceType?) {
        setFieldValue(HEART_RATE_SOURCE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("HeartRateAntplusDeviceType"))
    }

    /**
     * heart_rate_local_device_type: what heart_rate_source means when this message selects it.
     */
    public val heartRateLocalDeviceType: LocalDeviceType?
        get() = (getFieldValue(HEART_RATE_SOURCE_FIELD_NUM, 0, SubFieldSelector.Named("HeartRateLocalDeviceType")) as? UByte)?.let { LocalDeviceType.fromValue(it) }

    public fun setHeartRateLocalDeviceType(value: LocalDeviceType?) {
        setFieldValue(HEART_RATE_SOURCE_FIELD_NUM, value?.value, 0, SubFieldSelector.Named("HeartRateLocalDeviceType"))
    }

    /** travel_gas. Index of travel dive_gas message */
    public var travelGas: UShort?
        get() = getFieldValue(TRAVEL_GAS_FIELD_NUM) as? UShort
        set(value) = setFieldValue(TRAVEL_GAS_FIELD_NUM, value)

    /** ccr_low_setpoint_switch_mode. If low PO2 should be switched to automatically */
    public var ccrLowSetpointSwitchMode: CcrSetpointSwitchMode?
        get() = (getFieldValue(CCR_LOW_SETPOINT_SWITCH_MODE_FIELD_NUM) as? UByte)?.let { CcrSetpointSwitchMode.fromValue(it) }
        set(value) = setFieldValue(CCR_LOW_SETPOINT_SWITCH_MODE_FIELD_NUM, value?.value)

    /** ccr_low_setpoint, in percent. Target PO2 when using low setpoint */
    public var ccrLowSetpoint: Double?
        get() = getFieldValue(CCR_LOW_SETPOINT_FIELD_NUM) as? Double
        set(value) = setFieldValue(CCR_LOW_SETPOINT_FIELD_NUM, value)

    /** ccr_low_setpoint_depth, in m. Depth to switch to low setpoint in automatic mode */
    public var ccrLowSetpointDepth: Double?
        get() = getFieldValue(CCR_LOW_SETPOINT_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(CCR_LOW_SETPOINT_DEPTH_FIELD_NUM, value)

    /** ccr_high_setpoint_switch_mode. If high PO2 should be switched to automatically */
    public var ccrHighSetpointSwitchMode: CcrSetpointSwitchMode?
        get() = (getFieldValue(CCR_HIGH_SETPOINT_SWITCH_MODE_FIELD_NUM) as? UByte)?.let { CcrSetpointSwitchMode.fromValue(it) }
        set(value) = setFieldValue(CCR_HIGH_SETPOINT_SWITCH_MODE_FIELD_NUM, value?.value)

    /** ccr_high_setpoint, in percent. Target PO2 when using high setpoint */
    public var ccrHighSetpoint: Double?
        get() = getFieldValue(CCR_HIGH_SETPOINT_FIELD_NUM) as? Double
        set(value) = setFieldValue(CCR_HIGH_SETPOINT_FIELD_NUM, value)

    /** ccr_high_setpoint_depth, in m. Depth to switch to high setpoint in automatic mode */
    public var ccrHighSetpointDepth: Double?
        get() = getFieldValue(CCR_HIGH_SETPOINT_DEPTH_FIELD_NUM) as? Double
        set(value) = setFieldValue(CCR_HIGH_SETPOINT_DEPTH_FIELD_NUM, value)

    /** gas_consumption_display. Type of gas consumption rate to display. Some values are only valid if tank volume is known. */
    public var gasConsumptionDisplay: GasConsumptionRateType?
        get() = (getFieldValue(GAS_CONSUMPTION_DISPLAY_FIELD_NUM) as? UByte)?.let { GasConsumptionRateType.fromValue(it) }
        set(value) = setFieldValue(GAS_CONSUMPTION_DISPLAY_FIELD_NUM, value?.value)

    /** up_key_enabled. Indicates whether the up key is enabled during dives */
    public var upKeyEnabled: Boolean?
        get() = (getFieldValue(UP_KEY_ENABLED_FIELD_NUM) as? UByte)?.let { it.toInt() != 0 }
        set(value) = setFieldValue(UP_KEY_ENABLED_FIELD_NUM, value?.let { if (it) 1 else 0 })

    /** dive_sounds. Sounds and vibration enabled or disabled in-dive */
    public var diveSounds: Tone?
        get() = (getFieldValue(DIVE_SOUNDS_FIELD_NUM) as? UByte)?.let { Tone.fromValue(it) }
        set(value) = setFieldValue(DIVE_SOUNDS_FIELD_NUM, value?.value)

    /** last_stop_multiple. Usually 1.0/1.5/2.0 representing 3/4.5/6m or 10/15/20ft */
    public var lastStopMultiple: Double?
        get() = getFieldValue(LAST_STOP_MULTIPLE_FIELD_NUM) as? Double
        set(value) = setFieldValue(LAST_STOP_MULTIPLE_FIELD_NUM, value)

    /** no_fly_time_mode. Indicates which guidelines to use for no-fly surface interval. */
    public var noFlyTimeMode: NoFlyTimeMode?
        get() = (getFieldValue(NO_FLY_TIME_MODE_FIELD_NUM) as? UByte)?.let { NoFlyTimeMode.fromValue(it) }
        set(value) = setFieldValue(NO_FLY_TIME_MODE_FIELD_NUM, value?.value)

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val MESSAGE_INDEX_FIELD_NUM: UByte = 254u
        public const val NAME_FIELD_NUM: UByte = 0u
        public const val MODEL_FIELD_NUM: UByte = 1u
        public const val GF_LOW_FIELD_NUM: UByte = 2u
        public const val GF_HIGH_FIELD_NUM: UByte = 3u
        public const val WATER_TYPE_FIELD_NUM: UByte = 4u
        public const val WATER_DENSITY_FIELD_NUM: UByte = 5u
        public const val PO2_WARN_FIELD_NUM: UByte = 6u
        public const val PO2_CRITICAL_FIELD_NUM: UByte = 7u
        public const val PO2_DECO_FIELD_NUM: UByte = 8u
        public const val SAFETY_STOP_ENABLED_FIELD_NUM: UByte = 9u
        public const val BOTTOM_DEPTH_FIELD_NUM: UByte = 10u
        public const val BOTTOM_TIME_FIELD_NUM: UByte = 11u
        public const val APNEA_COUNTDOWN_ENABLED_FIELD_NUM: UByte = 12u
        public const val APNEA_COUNTDOWN_TIME_FIELD_NUM: UByte = 13u
        public const val BACKLIGHT_MODE_FIELD_NUM: UByte = 14u
        public const val BACKLIGHT_BRIGHTNESS_FIELD_NUM: UByte = 15u
        public const val BACKLIGHT_TIMEOUT_FIELD_NUM: UByte = 16u
        public const val REPEAT_DIVE_INTERVAL_FIELD_NUM: UByte = 17u
        public const val SAFETY_STOP_TIME_FIELD_NUM: UByte = 18u
        public const val HEART_RATE_SOURCE_TYPE_FIELD_NUM: UByte = 19u
        public const val HEART_RATE_SOURCE_FIELD_NUM: UByte = 20u
        public const val TRAVEL_GAS_FIELD_NUM: UByte = 21u
        public const val CCR_LOW_SETPOINT_SWITCH_MODE_FIELD_NUM: UByte = 22u
        public const val CCR_LOW_SETPOINT_FIELD_NUM: UByte = 23u
        public const val CCR_LOW_SETPOINT_DEPTH_FIELD_NUM: UByte = 24u
        public const val CCR_HIGH_SETPOINT_SWITCH_MODE_FIELD_NUM: UByte = 25u
        public const val CCR_HIGH_SETPOINT_FIELD_NUM: UByte = 26u
        public const val CCR_HIGH_SETPOINT_DEPTH_FIELD_NUM: UByte = 27u
        public const val GAS_CONSUMPTION_DISPLAY_FIELD_NUM: UByte = 29u
        public const val UP_KEY_ENABLED_FIELD_NUM: UByte = 30u
        public const val DIVE_SOUNDS_FIELD_NUM: UByte = 35u
        public const val LAST_STOP_MULTIPLE_FIELD_NUM: UByte = 36u
        public const val NO_FLY_TIME_MODE_FIELD_NUM: UByte = 37u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[254] = Field("MessageIndex", 254u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[0] = Field("Name", 0u, BaseType.STRING, 1.0, 0.0, "", false)
            fields[1] = Field("Model", 1u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[2] = Field("GfLow", 2u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[3] = Field("GfHigh", 3u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[4] = Field("WaterType", 4u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[5] = Field("WaterDensity", 5u, BaseType.FLOAT32, 1.0, 0.0, "kg/m^3", false)
            fields[6] = Field("Po2Warn", 6u, BaseType.UINT8, 100.0, 0.0, "percent", false)
            fields[7] = Field("Po2Critical", 7u, BaseType.UINT8, 100.0, 0.0, "percent", false)
            fields[8] = Field("Po2Deco", 8u, BaseType.UINT8, 100.0, 0.0, "percent", false)
            fields[9] = Field("SafetyStopEnabled", 9u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[10] = Field("BottomDepth", 10u, BaseType.FLOAT32, 1.0, 0.0, "", false)
            fields[11] = Field("BottomTime", 11u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[12] = Field("ApneaCountdownEnabled", 12u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[13] = Field("ApneaCountdownTime", 13u, BaseType.UINT32, 1.0, 0.0, "", false)
            fields[14] = Field("BacklightMode", 14u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[15] = Field("BacklightBrightness", 15u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[16] = Field("BacklightTimeout", 16u, BaseType.UINT8, 1.0, 0.0, "", false)
            fields[17] = Field("RepeatDiveInterval", 17u, BaseType.UINT16, 1.0, 0.0, "s", false)
            fields[18] = Field("SafetyStopTime", 18u, BaseType.UINT16, 1.0, 0.0, "s", false)
            fields[19] = Field("HeartRateSourceType", 19u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[20] = Field("HeartRateSource", 20u, BaseType.UINT8, 1.0, 0.0, "", false)
                .addSubField(
                    SubField("HeartRateAntplusDeviceType", BaseType.UINT8, 1.0, 0.0, "").also { subField ->
                        subField.addMap(19u, 1)
                    },
                )
                .addSubField(
                    SubField("HeartRateLocalDeviceType", BaseType.UINT8, 1.0, 0.0, "").also { subField ->
                        subField.addMap(19u, 5)
                    },
                )
            fields[21] = Field("TravelGas", 21u, BaseType.UINT16, 1.0, 0.0, "", false)
            fields[22] = Field("CcrLowSetpointSwitchMode", 22u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[23] = Field("CcrLowSetpoint", 23u, BaseType.UINT8, 100.0, 0.0, "percent", false)
            fields[24] = Field("CcrLowSetpointDepth", 24u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[25] = Field("CcrHighSetpointSwitchMode", 25u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[26] = Field("CcrHighSetpoint", 26u, BaseType.UINT8, 100.0, 0.0, "percent", false)
            fields[27] = Field("CcrHighSetpointDepth", 27u, BaseType.UINT32, 1000.0, 0.0, "m", false)
            fields[29] = Field("GasConsumptionDisplay", 29u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[30] = Field("UpKeyEnabled", 30u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[35] = Field("DiveSounds", 35u, BaseType.ENUM, 1.0, 0.0, "", false)
            fields[36] = Field("LastStopMultiple", 36u, BaseType.UINT8, 10.0, 0.0, "", false)
            fields[37] = Field("NoFlyTimeMode", 37u, BaseType.ENUM, 1.0, 0.0, "", false)
            return fields
        }
    }
}
